import { useState } from "react";
import { ArrowDown, ArrowUp, Pencil, Plus, SlidersHorizontal } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampSelection, MentionObligatoire } from "@/composants/ui/champ";
import { Chargement, Erreur } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import { useReferentiel } from "@/lib/referentiels";
import { horodatage } from "@/lib/libelles";
import {
  api,
  type QualiteSignataire,
  type Referentiel,
  type ReglagesTypeDocument,
  type ValeurReferentiel,
} from "@/lib/api";

/**
 * Les référentiels administrables (ADR-0030).
 *
 *     « L'administrateur puisse créer, modifier, ordonner, activer ou
 *       désactiver les catégories directement depuis LABGEST, sans
 *       intervention sur le code. » — LAB, 18/09 §2
 *
 * Le CODE est stable : il se choisit à la création et ne se modifie plus —
 * c'est lui que les commandes, les lots et l'historique référencent. Le libellé se
 * renomme. On désactive, on ne supprime pas. Chaque modification est
 * historisée par la base, avec son auteur.
 *
 * Un seul écran pour tous les référentiels, qu'il lit du registre de l'API
 * (`domaine/referentiels.py`) : un référentiel de plus est une entrée de plus
 * là-bas, et aucune ligne ici. Aucun n'est livré avec des valeurs :
 * l'Administrateur les initialise (LAB, 24/09 ; ADR-0031).
 */
export function PageReferentiels() {
  const registre = useChargement((signal) => api.registreReferentiels(signal), []);
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-encre">Référentiels</h1>
        <p className="mt-1 text-sm text-encre-douce">
          Les catégories métier, réglables sans toucher au code. Le code d'une valeur ne change
          jamais ; son libellé, son ordre et son activation, si.
        </p>
      </div>
      {registre.erreur ? (
        <Erreur message={registre.erreur} onReessayer={registre.recharger} />
      ) : null}
      {!registre.erreur && registre.enCours ? (
        <Chargement message="Chargement des référentiels…" />
      ) : null}
      {(registre.donnees ?? []).map((ref) => (
        <SectionReferentiel key={ref.nom} nom={ref.nom} titre={ref.libelle} />
      ))}
    </div>
  );
}

function SectionReferentiel({ nom, titre }: { nom: string; titre: string }) {
  const charge = useReferentiel(nom);
  const [courant, setCourant] = useState<Referentiel | null>(null);
  const [ajout, setAjout] = useState(false);
  const [enCorrection, setEnCorrection] = useState<string | null>(null);
  const [enReglage, setEnReglage] = useState<string | null>(null);
  const [action, setAction] = useState<string | null>(null);

  const referentiel = courant ?? charge.donnees;
  if (charge.erreur) return <Erreur message={charge.erreur} onReessayer={charge.recharger} />;
  if (!referentiel || charge.enCours) return <Chargement message={`Chargement : ${titre}…`} />;

  const valeurs = referentiel.valeurs;

  async function agir(travail: () => Promise<Referentiel>) {
    setAction(null);
    try {
      setCourant(await travail());
      return true;
    } catch (cause) {
      setAction(messageDe(cause));
      return false;
    }
  }

  function deplacer(index: number, sens: -1 | 1) {
    const ids = valeurs.map((v) => v.id);
    const cible = index + sens;
    if (cible < 0 || cible >= ids.length) return;
    [ids[index], ids[cible]] = [ids[cible], ids[index]];
    void agir(() => api.ordonnerReferentiel(nom, ids));
  }

  return (
    <Carte>
      <EnTeteCarte className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-encre">{titre}</h2>
        {!ajout ? (
          <Bouton taille="compact" onClick={() => setAjout(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            Ajouter une valeur
          </Bouton>
        ) : null}
      </EnTeteCarte>
      {action ? (
        <CorpsCarte>
          <Erreur message={action} />
        </CorpsCarte>
      ) : null}
      {ajout ? (
        <CorpsCarte className="border-b border-filet">
          <FormulaireValeur
            onAnnuler={() => setAjout(false)}
            onValider={async (code, libelle) => {
              if (await agir(() => api.creerValeurReferentiel(nom, code, libelle))) setAjout(false);
            }}
          />
        </CorpsCarte>
      ) : null}
      {valeurs.length === 0 && !ajout ? (
        <CorpsCarte>
          <p className="text-sm text-encre-douce">
            Aucune valeur : LABGEST n'en livre aucune, c'est à l'Administrateur de les créer.
          </p>
        </CorpsCarte>
      ) : null}
      <ul className="divide-y divide-filet">
        {valeurs.map((valeur, index) => (
          <li id={`valeur-${nom}-${valeur.code}`} key={valeur.id} className="flex flex-col gap-2 px-4 py-3">
            {enCorrection === valeur.id ? (
              <FormulaireValeur
                initial={valeur}
                onAnnuler={() => setEnCorrection(null)}
                onValider={async (_code, libelle) => {
                  if (await agir(() => api.corrigerValeurReferentiel(nom, valeur.id, { libelle })))
                    setEnCorrection(null);
                }}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="break-all font-mono text-xs text-encre-douce sm:w-44 sm:shrink-0">
                  {valeur.code}
                </span>
                <span className="flex-1 text-sm font-medium text-encre">{valeur.libelle}</span>
                {/* L'état se lit en toutes lettres (ADR-0024). */}
                <Etiquette ton={valeur.actif ? "actif" : "neutre"}>
                  {valeur.actif ? "Active" : "Désactivée"}
                </Etiquette>
                <span className="text-xs text-encre-douce">modifiée le {horodatage(valeur.maj_le)}</span>
                <span className="flex flex-wrap items-center gap-1">
                  <Bouton
                    taille="compact"
                    variante="discret"
                    aria-label={`Monter ${valeur.libelle}`}
                    disabled={index === 0}
                    onClick={() => deplacer(index, -1)}
                  >
                    <ArrowUp className="h-4 w-4" aria-hidden />
                  </Bouton>
                  <Bouton
                    taille="compact"
                    variante="discret"
                    aria-label={`Descendre ${valeur.libelle}`}
                    disabled={index === valeurs.length - 1}
                    onClick={() => deplacer(index, 1)}
                  >
                    <ArrowDown className="h-4 w-4" aria-hidden />
                  </Bouton>
                  <Bouton
                    taille="compact"
                    variante="discret"
                    onClick={() => setEnCorrection(valeur.id)}
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                    Renommer
                  </Bouton>
                  {valeur.reglages ? (
                    <Bouton
                      taille="compact"
                      variante="discret"
                      onClick={() => setEnReglage(valeur.id)}
                    >
                      <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
                      Régler
                    </Bouton>
                  ) : null}
                  <Bouton
                    taille="compact"
                    variante="discret"
                    onClick={() =>
                      void agir(() =>
                        api.corrigerValeurReferentiel(nom, valeur.id, { actif: !valeur.actif }),
                      )
                    }
                  >
                    {valeur.actif ? "Désactiver" : "Réactiver"}
                  </Bouton>
                </span>
              </div>
            )}
            {valeur.reglages && enReglage === valeur.id ? (
              <FormulaireReglages
                initial={valeur.reglages}
                onAnnuler={() => setEnReglage(null)}
                onValider={async (reglages) => {
                  if (await agir(() => api.reglerTypeDocument(valeur.id, reglages)))
                    setEnReglage(null);
                }}
              />
            ) : valeur.reglages ? (
              <ResumeReglages reglages={valeur.reglages} />
            ) : null}
          </li>
        ))}
      </ul>
      <CorpsCarte className="border-t border-filet">
        <p className="text-xs text-encre-douce">
          Une valeur désactivée n'est plus proposée aux nouvelles saisies ; ce qui la porte la
          garde. Rien ne se supprime : l'historique et les fiches existantes y font référence.
        </p>
      </CorpsCarte>
    </Carte>
  );
}

/** Une valeur à créer (code et libellé), ou à renommer (libellé seul). */
function FormulaireValeur({
  initial,
  onValider,
  onAnnuler,
}: {
  initial?: ValeurReferentiel;
  onValider: (code: string, libelle: string) => Promise<void>;
  onAnnuler: () => void;
}) {
  const [code, setCode] = useState(initial?.code ?? "");
  const [libelle, setLibelle] = useState(initial?.libelle ?? "");
  const [envoi, setEnvoi] = useState(false);

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        try {
          await onValider(code, libelle);
        } finally {
          setEnvoi(false);
        }
      }}
    >
      {initial ? null : <MentionObligatoire />}
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ
          libelle="Code"
          required
          disabled={initial !== undefined}
          maxLength={40}
          placeholder="TERRASSE"
          aide={
            initial
              ? "Le code ne change jamais : il est référencé par les lots et l'historique."
              : "Majuscules, chiffres et tirets bas. Définitif une fois créé."
          }
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
        />
        <Champ
          libelle="Libellé"
          required
          maxLength={120}
          value={libelle}
          onChange={(e) => setLibelle(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" taille="compact" disabled={envoi}>
          {envoi ? "Enregistrement…" : initial ? "Renommer" : "Créer"}
        </Bouton>
        <Bouton type="button" variante="discret" taille="compact" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

const DEMANDES: { valeur: ReglagesTypeDocument["demande"]; libelle: string }[] = [
  { valeur: "AUCUNE", libelle: "Ni validation ni signature" },
  { valeur: "VALIDATION", libelle: "Une validation" },
  { valeur: "SIGNATURE", libelle: "Une ou plusieurs signatures" },
];

const CLASSES: { valeur: ReglagesTypeDocument["classe_conservation"]; libelle: string }[] = [
  { valeur: "PREUVE_CHANTIER", libelle: "preuve de chantier" },
  { valeur: "MARKETING_AUTORISE", libelle: "marketing autorisé" },
  { valeur: "PIECE_PROBANTE", libelle: "pièce probante" },
];

const QUALITES: { valeur: QualiteSignataire; libelle: string; champ: string }[] = [
  { valeur: "CLIENT", libelle: "client", champ: "Signatures du client" },
  { valeur: "COLLABORATEUR", libelle: "collaborateur", champ: "Signatures d'un collaborateur" },
  { valeur: "INTERVENANT", libelle: "intervenant", champ: "Signatures d'un intervenant" },
];

/**
 * Ce que LAB règle par type de document (ADR-0062), lu en toutes lettres : la
 * page imprimée en noir et blanc ne perd rien (ADR-0024).
 */
function ResumeReglages({ reglages }: { reglages: ReglagesTypeDocument }) {
  const signataires = reglages.signataires_attendus
    .map((q) => QUALITES.find((qualite) => qualite.valeur === q)?.libelle ?? q)
    .join(", ");
  const demande =
    reglages.demande === "AUCUNE"
      ? "Ni validation ni signature"
      : reglages.demande === "VALIDATION"
        ? "Demande une validation"
        : reglages.signataires_attendus.length === 1
          ? `Demande 1 signature : ${signataires}`
          : `Demande ${reglages.signataires_attendus.length} signatures : ${signataires}`;
  const classe = CLASSES.find((c) => c.valeur === reglages.classe_conservation)?.libelle;
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-encre-douce sm:pl-48">
      <li>{demande}</li>
      <li>{reglages.publiable_client ? "Publiable au client" : "Non publiable au client"}</li>
      <li>{reglages.exigible ? "Exigible comme pièce" : "Non exigible comme pièce"}</li>
      <li>Conservation : {classe}</li>
    </ul>
  );
}

/** Les réglages d'un type, d'un bloc. La base refuse une signature sans signataire. */
function FormulaireReglages({
  initial,
  onValider,
  onAnnuler,
}: {
  initial: ReglagesTypeDocument;
  onValider: (reglages: ReglagesTypeDocument) => Promise<void>;
  onAnnuler: () => void;
}) {
  const [demande, setDemande] = useState(initial.demande);
  const [nombres, setNombres] = useState<Record<QualiteSignataire, number>>(() => ({
    CLIENT: initial.signataires_attendus.filter((q) => q === "CLIENT").length,
    COLLABORATEUR: initial.signataires_attendus.filter((q) => q === "COLLABORATEUR").length,
    INTERVENANT: initial.signataires_attendus.filter((q) => q === "INTERVENANT").length,
  }));
  const [publiable, setPubliable] = useState(initial.publiable_client);
  const [exigible, setExigible] = useState(initial.exigible);
  const [classe, setClasse] = useState(initial.classe_conservation);
  const [envoi, setEnvoi] = useState(false);

  return (
    <form
      className="flex flex-col gap-3 rounded-[6px] border border-filet bg-chene-clair/20 p-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        try {
          await onValider({
            demande,
            // Sans signature demandée, aucun signataire n'est attendu.
            signataires_attendus:
              demande === "SIGNATURE"
                ? QUALITES.flatMap((q) => Array<QualiteSignataire>(nombres[q.valeur]).fill(q.valeur))
                : [],
            publiable_client: publiable,
            exigible,
            classe_conservation: classe,
          });
        } finally {
          setEnvoi(false);
        }
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <ChampSelection
          libelle="Ce que le type demande"
          options={DEMANDES}
          value={demande}
          onChange={(e) => setDemande(e.target.value as ReglagesTypeDocument["demande"])}
        />
        <ChampSelection
          libelle="Classe de conservation"
          options={CLASSES.map((c) => ({ ...c, libelle: c.libelle[0].toUpperCase() + c.libelle.slice(1) }))}
          value={classe}
          onChange={(e) =>
            setClasse(e.target.value as ReglagesTypeDocument["classe_conservation"])
          }
        />
      </div>
      {demande === "SIGNATURE" ? (
        <div className="grid gap-3 sm:grid-cols-3">
          {QUALITES.map((q) => (
            <Champ
              key={q.valeur}
              libelle={q.champ}
              type="number"
              min={0}
              max={5}
              value={nombres[q.valeur]}
              onChange={(e) =>
                setNombres((avant) => ({
                  ...avant,
                  [q.valeur]: Math.max(0, Math.min(5, Number(e.target.value) || 0)),
                }))
              }
            />
          ))}
        </div>
      ) : null}
      <div className="flex flex-col gap-2 text-sm text-encre sm:flex-row sm:gap-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={publiable}
            onChange={(e) => setPubliable(e.target.checked)}
          />
          Publiable au client
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={exigible} onChange={(e) => setExigible(e.target.checked)} />
          Exigible comme pièce obligatoire
        </label>
      </div>
      <p className="text-xs text-encre-douce">
        Un document de commande fournisseur, de réception ou de conditions commerciales ne se
        publie jamais au client, quel que soit ce réglage.
      </p>
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" taille="compact" disabled={envoi}>
          {envoi ? "Enregistrement…" : "Enregistrer les réglages"}
        </Bouton>
        <Bouton type="button" variante="discret" taille="compact" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
